import React, { useState, useEffect } from 'react';
import { 
  Calendar, Clock, CheckCircle2, XCircle, AlertCircle,
  Search, Filter, Palmtree, Heart, Briefcase, Baby,
  ChevronDown, User, MessageSquare, Send, RotateCcw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { db } from '../../../lib/db';
import { formatDate, getDaysBetween } from '../../../lib/dateUtils';
import { useAuthStore } from '../../../store/authStore';
import { toast } from 'sonner';

const leaveTypeInfo = {
  annual: { label: 'Annual Leave', icon: Palmtree, color: 'bg-blue-100 text-blue-600' },
  sick: { label: 'Sick Leave', icon: Heart, color: 'bg-red-100 text-red-600' },
  casual: { label: 'Casual Leave', icon: Briefcase, color: 'bg-amber-100 text-amber-600' },
  maternity: { label: 'Maternity Leave', icon: Baby, color: 'bg-pink-100 text-pink-600' },
  paternity: { label: 'Paternity Leave', icon: Baby, color: 'bg-cyan-100 text-cyan-600' },
  compensatory: { label: 'Comp Off', icon: Clock, color: 'bg-purple-100 text-purple-600' }
};

export default function LeaveManagement() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [comments, setComments] = useState('');

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user?.id) return;
    try {
      // Get team members
      const allUsers = await db.users.toArray();
      const team = allUsers.filter(u => u.supervisorId === user.id && u.status === 'active');
      setTeamMembers(team);

      // Get leave requests for team members
      const teamIds = team.map(t => t.id);
      const allRequests = await db.leaveRequests.toArray();
      const teamRequests = allRequests.filter(r => teamIds.includes(r.userId));
      
      // Enrich with user data
      const enriched = teamRequests.map(r => ({
        ...r,
        employee: team.find(t => t.id === r.userId)
      })).sort((a, b) => b.createdAt - a.createdAt);

      setLeaveRequests(enriched);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setComments('');
    setShowActionModal(true);
  };

  const confirmAction = async () => {
    if (!selectedRequest) return;

    try {
      // Handle send to CEO action
      if (actionType === 'send_to_ceo') {
        await db.leaveRequests.update(selectedRequest.id, {
          status: 'pending_ceo',
          forwardedBy: user.id,
          forwardedAt: Date.now(),
          supervisorComments: comments,
          updatedAt: Date.now()
        });
        toast.success('Leave request sent to CEO for review');
        setShowActionModal(false);
        loadData();
        return;
      }

      const newStatus = actionType === 'approve' ? 'approved' : 'rejected';
      
      // Update leave request
      await db.leaveRequests.update(selectedRequest.id, {
        status: newStatus,
        approvedBy: user.id,
        reviewedBy: user.id,
        reviewedAt: Date.now(),
        reviewComments: comments,
        rejectionReason: actionType === 'reject' ? comments : null,
        updatedAt: Date.now()
      });

      // Update leave balance if approved
      if (newStatus === 'approved') {
        const leaveType = selectedRequest.leaveType;
        const balance = await db.leaveBalances.where('userId').equals(selectedRequest.userId).first();
        
        if (balance && balance[leaveType]) {
          await db.leaveBalances.where('userId').equals(selectedRequest.userId).modify({
            [`${leaveType}.used`]: (balance[leaveType].used || 0) + selectedRequest.days,
            [`${leaveType}.pending`]: Math.max(0, (balance[leaveType].pending || 0) - selectedRequest.days)
          });
        }
      } else {
        // Rejected - restore pending balance
        const leaveType = selectedRequest.leaveType;
        const balance = await db.leaveBalances.where('userId').equals(selectedRequest.userId).first();
        
        if (balance && balance[leaveType]) {
          await db.leaveBalances.where('userId').equals(selectedRequest.userId).modify({
            [`${leaveType}.pending`]: Math.max(0, (balance[leaveType].pending || 0) - selectedRequest.days)
          });
        }
      }

      toast.success(`Leave request ${newStatus}`);
      setShowActionModal(false);
      loadData();
    } catch (error) {
      console.error('Error updating request:', error);
      toast.error('Failed to update request');
    }
  };

  const getStatusBadge = (request) => {
    const status = request.status;
    switch (status) {
      case 'approved':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      case 'pending_ceo':
        return <Badge className="bg-purple-100 text-purple-700"><Send className="w-3 h-3 mr-1" />Sent to CEO</Badge>;
      default:
        if (request.sentBackBy) {
          return <Badge className="bg-orange-100 text-orange-700"><RotateCcw className="w-3 h-3 mr-1" />Returned by CEO</Badge>;
        }
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  const filteredRequests = leaveRequests.filter(request => {
    const matchesSearch = searchQuery === '' ||
      request.employee?.profile?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.employee?.profile?.lastName?.toLowerCase().includes(searchQuery.toLowerCase());
    
    let matchesStatus = false;
    if (statusFilter === 'all') {
      matchesStatus = true;
    } else if (statusFilter === 'sent_back') {
      matchesStatus = request.status === 'pending' && request.sentBackBy;
    } else {
      matchesStatus = request.status === statusFilter;
    }
    
    return matchesSearch && matchesStatus;
  });

  const pendingCount = leaveRequests.filter(r => r.status === 'pending').length;
  const approvedCount = leaveRequests.filter(r => r.status === 'approved').length;
  const rejectedCount = leaveRequests.filter(r => r.status === 'rejected').length;
  const sentToCeoCount = leaveRequests.filter(r => r.status === 'pending_ceo').length;
  const sentBackCount = leaveRequests.filter(r => r.status === 'pending' && r.sentBackBy).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leave Management</h1>
          <p className="text-gray-500 mt-1">Review and approve team leave requests</p>
        </div>
        {pendingCount > 0 && (
          <Badge className="bg-amber-100 text-amber-700 text-base px-4 py-2">
            <AlertCircle className="w-4 h-4 mr-2" />
            {pendingCount} Pending Request{pendingCount !== 1 ? 's' : ''}
          </Badge>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Requests</p>
                <p className="text-2xl font-bold text-gray-900">{leaveRequests.length}</p>
              </div>
              <Calendar className="w-8 h-8 text-violet-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-amber-600">{pendingCount}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Approved</p>
                <p className="text-2xl font-bold text-emerald-600">{approvedCount}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Rejected</p>
                <p className="text-2xl font-bold text-red-600">{rejectedCount}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by employee name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Requests</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="pending_ceo">Sent to CEO</SelectItem>
                <SelectItem value="sent_back">Returned by CEO</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Leave Requests List */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>Leave Requests</CardTitle>
          <CardDescription>
            {filteredRequests.length} request{filteredRequests.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredRequests.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-4 opacity-20" />
              <p>No leave requests found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRequests.map(request => {
                const typeInfo = leaveTypeInfo[request.leaveType] || leaveTypeInfo.casual;
                const TypeIcon = typeInfo.icon;
                
                return (
                  <div
                    key={request.id}
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                          {request.employee?.profile?.firstName?.[0]}
                          {request.employee?.profile?.lastName?.[0]}
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {request.employee?.profile?.firstName} {request.employee?.profile?.lastName}
                          </h4>
                          <p className="text-sm text-gray-500">
                            {request.employee?.profile?.position || 'Employee'}
                          </p>
                          <div className="flex items-center gap-3 mt-2">
                            <Badge className={typeInfo.color}>
                              <TypeIcon className="w-3 h-3 mr-1" />
                              {typeInfo.label}
                            </Badge>
                            {getStatusBadge(request)}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col md:items-end gap-2">
                        <div className="text-sm">
                          <span className="text-gray-500">Duration: </span>
                          <span className="font-medium">{request.days} day{request.days !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatDate(request.startDate)} - {formatDate(request.endDate)}
                        </div>
                        {request.status === 'pending' && (
                          <div className="flex gap-2 mt-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-purple-600 hover:bg-purple-50"
                              onClick={() => handleAction(request, 'send_to_ceo')}
                            >
                              <Send className="w-4 h-4 mr-1" />
                              Send to CEO
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleAction(request, 'reject')}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => handleAction(request, 'approve')}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    {request.reason && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Reason: </span>
                          {request.reason}
                        </p>
                      </div>
                    )}

                    {request.sentBackBy && request.ceoComments && (
                      <div className="mt-2 p-2 bg-orange-50 rounded text-sm">
                        <span className="font-medium text-orange-700">CEO Comments: </span>
                        {request.ceoComments}
                      </div>
                    )}

                    {request.reviewComments && (
                      <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                        <span className="font-medium">Review Comments: </span>
                        {request.reviewComments}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Modal */}
      <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' && 'Approve Leave Request'}
              {actionType === 'reject' && 'Reject Leave Request'}
              {actionType === 'send_to_ceo' && 'Send to CEO for Review'}
            </DialogTitle>
            <DialogDescription>
              {selectedRequest && (
                <span>
                  {selectedRequest.employee?.profile?.firstName} {selectedRequest.employee?.profile?.lastName} - 
                  {' '}{selectedRequest.days} day{selectedRequest.days !== 1 ? 's' : ''} 
                  {' '}({leaveTypeInfo[selectedRequest.leaveType]?.label || 'Leave'})
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="comments">
                {actionType === 'reject' ? 'Rejection Reason' : 'Comments'} 
                {actionType === 'reject' && <span className="text-red-500 ml-1">*</span>}
              </Label>
              <Textarea
                id="comments"
                placeholder={
                  actionType === 'approve' 
                    ? "Add any notes for the employee..." 
                    : actionType === 'reject'
                    ? "Please provide a reason for rejection..."
                    : "Add notes for CEO review..."
                }
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="mt-1"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={confirmAction}
              disabled={actionType === 'reject' && !comments.trim()}
              className={
                actionType === 'approve' 
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : actionType === 'reject'
                  ? "bg-red-600 hover:bg-red-700 text-white"
                  : "bg-purple-600 hover:bg-purple-700 text-white"
              }
            >
              {actionType === 'approve' && (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Approve Request
                </>
              )}
              {actionType === 'reject' && (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject Request
                </>
              )}
              {actionType === 'send_to_ceo' && (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send to CEO
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
